import React, { useCallback, useMemo } from "react";
import { searchFacilityLocations } from "./api";
import { RecordTypeahead } from "./RecordTypeahead";

/**
 * Facility suggestion field for the walk-up check-in's Origin Name / Destination Name.
 *
 * The dropdown mechanics live in {@link RecordTypeahead}; what remains here is everything
 * facility-specific — the search call, what a row reads as, and the office scoping. `onPick`
 * reports the whole Location so the caller can fill the address fields beside it in one go,
 * while `onChange` reports only the typed name: a facility we have never seen must still be
 * enterable, which is the whole point of a walk-up.
 */
export function FacilityTypeahead({
  label,
  value,
  onChange,
  onPick,
  officeCode,
  className = "",
  disabled = false,
  minChars = 2,
  placeholder,
}) {
  const oc = String(officeCode ?? "").trim();

  const search = useCallback((query) => searchFacilityLocations(oc, query), [oc]);

  const getLabel = useCallback((loc) => String(loc?.locationName ?? "").trim(), []);

  const getSecondary = useMemo(
    () => (loc) =>
      [loc?.addressLine1, loc?.city, loc?.state, loc?.zipCode, loc?.country]
        .map((p) => String(p ?? "").trim())
        .filter(Boolean)
        .join(", "),
    []
  );

  const getKey = useCallback((loc, i) => String(loc?.id ?? loc?.locationRef ?? i), []);

  return (
    <RecordTypeahead
      label={label}
      value={value}
      onChange={onChange}
      onPick={onPick}
      search={search}
      getLabel={getLabel}
      getSecondary={getSecondary}
      getKey={getKey}
      // No office code means the facility search has nothing to scope to, so it is held off
      // entirely rather than fired and rejected.
      enabled={Boolean(oc)}
      className={className}
      disabled={disabled}
      minChars={minChars}
      placeholder={placeholder}
    />
  );
}

export default FacilityTypeahead;
